import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { ArrowLeft, Edit, Plus, X, XCircle } from '@ui/components/icons';
import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import {
  calculateSpreadPercentage,
  useSpreadPercentage,
} from '@ui/features/Spread/useSpreadPercentage';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { useTokenSizeDisplayDecimals } from '@ui/hooks/useTokenSizeDisplayDecimals';
import { toBigValue, toReadableValue } from '@ui/lib/bignumber';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import {
  getActiveDecreaseOrders,
  getActiveSlOrders,
  getActiveTpOrders,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import {
  DecreaseOrderKind,
  type DecreaseOrderRow,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import {
  pollV2IntentStatus,
} from '@ui/services/backend/oneClickV2Service';
import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import BigNumber from 'bignumber.js';
import { useMemo, useRef, useState, useCallback } from 'react';
import { Box, Grid, HStack, Stack, styled, VStack } from 'styled-system/jsx';

import { useGetTargetMaxTpRatios } from '../../hooks/useGetTargetMaxTpRatios';
import { useInvalidatePositionQuery } from '../../hooks/useInvalidateQuery';
import { useLiqPrice } from '../../hooks/useLiqPrice';
import type { GenericTradeParams, TpSlValueState } from '../../types';
import {
  EditDecreaseOrderForm,
  SummaryStat,
} from '../DecreaseOrder/EditDecreaseOrderForm';
import { CloseQtyPercentageSelector } from '../ClosePosition/CloseQtyPercentageSelector';
import { TradeLeverageBadge } from '../LeverageBadge';
import { formatLeverage } from '../TradeModule/utils/formatLeverage';
import { EditTpSl } from './components/EditTpSl';
import {
  useEditSlValidation,
  useEditTpValidation,
} from './hooks/useEditTpSlValidation';
import { valueStyle } from './styles';

type OrderFilter = 'ALL' | 'TP' | 'SL';

const FILTERS: { value: OrderFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'TP', label: 'Take-Profit' },
  { value: 'SL', label: 'Stop-Loss' },
];

export const MergedTpSlOrdersOverlay = ({
  children,
  genericTradeParams,
  activeDecreaseOrders,
  collateralMarketPrice,
  pairMarketPrice,
  onOpenChange,
  ...rest
}: Omit<Dialog.RootProps, 'type'> & {
  activeDecreaseOrders?: DecreaseOrderRow[];
  collateralMarketPrice: BigNumber;
  pairMarketPrice: BigNumber;
  children?: React.ReactNode;
} & GenericTradeParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<OrderFilter>('ALL');
  const [isAdding, setIsAdding] = useState(false);
  const [editingOrder, setEditingOrder] = useState<DecreaseOrderRow | null>(
    null,
  );
  const keepOpenOnViewChangeRef = useRef(false);
  const keepOpenOnViewChange = () => {
    keepOpenOnViewChangeRef.current = true;
    window.setTimeout(() => {
      keepOpenOnViewChangeRef.current = false;
    }, 100);
  };
  const activeOrders = getActiveDecreaseOrders(activeDecreaseOrders);
  const visibleOrders = useMemo(() => {
    if (filter === 'TP') return getActiveTpOrders(activeOrders);
    if (filter === 'SL') return getActiveSlOrders(activeOrders);

    return activeOrders;
  }, [activeOrders, filter]);

  const livePairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
    genericTradeParams.pair.priceDisplayDecimals,
  );

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      {...rest}
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open && keepOpenOnViewChangeRef.current) {
          keepOpenOnViewChangeRef.current = false;
          setIsOpen(true);
          return;
        }

        setIsOpen(details.open);
        onOpenChange?.(details);

        if (!details.open) {
          setIsAdding(false);
          setEditingOrder(null);
          setFilter('ALL');
        }
      }}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            w={isAdding || editingOrder ? '430px' : '620px'}
            maxW="calc(100vw - 32px)"
            maxH="calc(100vh - 48px)"
            overflow="hidden"
            data-cy="MergedTpSlOrdersModalContent"
          >
            <Stack gap={0}>
              {editingOrder ? (
                <EditDecreaseOrderView
                  order={editingOrder}
                  genericTradeParams={genericTradeParams}
                  onBack={() => {
                    keepOpenOnViewChange();
                    setEditingOrder(null);
                  }}
                >
                  <EditDecreaseOrderForm
                    order={editingOrder}
                    genericTradeParams={genericTradeParams}
                    collateralMarketPrice={collateralMarketPrice}
                    pairMarketPrice={pairMarketPrice}
                    onDone={() => setEditingOrder(null)}
                  />
                </EditDecreaseOrderView>
              ) : isAdding ? (
                <AddDecreaseOrderView
                  genericTradeParams={genericTradeParams}
                  onBack={() => {
                    keepOpenOnViewChange();
                    setIsAdding(false);
                  }}
                >
                  <DecreaseOrderForm
                    genericTradeParams={genericTradeParams}
                    collateralMarketPrice={collateralMarketPrice}
                    pairMarketPrice={pairMarketPrice}
                    onDone={() => setIsAdding(false)}
                  />
                </AddDecreaseOrderView>
              ) : (
                <>
                  <OrdersHeader
                    genericTradeParams={genericTradeParams}
                    activeOrderCount={activeOrders.length}
                  />

                  <Grid
                    px="4"
                    py="3"
                    gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                    columnGap="3"
                    borderBottom="1px solid"
                    borderBottomColor="v2.border.hairline"
                  >
                    <Metric label="Entry price">
                      <AmountUsd
                        value={genericTradeParams.entryPrice}
                        css={metricValueStyle}
                        opts={{
                          displayDecimals: priceDisplayDecimals,
                        }}
                      />
                    </Metric>
                    <Metric label="Mark price">
                      <AmountUsd
                        value={livePairMarketPrice ?? pairMarketPrice}
                        css={metricValueStyle}
                        opts={{
                          displayDecimals: priceDisplayDecimals,
                        }}
                      />
                    </Metric>
                    <Metric label="Liquidation price">
                      <LiqPriceValue genericTradeParams={genericTradeParams} />
                    </Metric>
                  </Grid>

                  <HStack
                    px="3"
                    borderBottom="1px solid"
                    borderBottomColor="v2.border.default"
                    justifyContent="space-between"
                  >
                    <HStack gap="1" h="40px">
                      {FILTERS.map((item) => (
                        <styled.button
                          key={item.value}
                          type="button"
                          h="40px"
                          px="3"
                          borderRadius="0"
                          border="none"
                          borderBottom="2px solid"
                          bg="transparent"
                          color={
                            filter === item.value
                              ? 'v2.text.primary'
                              : 'v2.text.muted'
                          }
                          borderBottomColor={
                            filter === item.value
                              ? 'v2.long.default'
                              : 'transparent'
                          }
                          textStyle="v2.ui"
                          whiteSpace="nowrap"
                          cursor="pointer"
                          transition="background 160ms cubic-bezier(0.2, 0, 0.2, 1), color 160ms cubic-bezier(0.2, 0, 0.2, 1)"
                          _hover={{
                            bg: 'v2.bg.surface1',
                            color: 'v2.text.secondary',
                          }}
                          onClick={() => setFilter(item.value)}
                        >
                          {item.label}
                        </styled.button>
                      ))}
                    </HStack>
                    <Button
                      variant="ghost"
                      size="small"
                      color="v2.text.secondary"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        keepOpenOnViewChange();
                        setIsAdding(true);
                      }}
                    >
                      Add TP/SL
                      <Icon ml="1" boxSize="14px">
                        <Plus />
                      </Icon>
                    </Button>
                  </HStack>

                  <Stack maxH="420px" overflowY="auto" p="3" gap="2">
                    {visibleOrders.length > 0 ? (
                      visibleOrders.map((order) => (
                        <DecreaseOrderItem
                          key={order.orderHash}
                          genericTradeParams={genericTradeParams}
                          order={order}
                          onEdit={() => {
                            keepOpenOnViewChange();
                            setEditingOrder(order);
                          }}
                        />
                      ))
                    ) : (
                      <VStack minH="156px" justifyContent="center" gap="3">
                        <Text textStyle="v2.ui" color="v2.text.subtle">
                          No resting orders
                        </Text>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            keepOpenOnViewChange();
                            setIsAdding(true);
                          }}
                        >
                          Add TP/SL
                        </Button>
                      </VStack>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Metric = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Stack gap="1" alignItems="flex-start" minW={0}>
    <Text textStyle="v2.meta" color="v2.text.muted">
      {label}
    </Text>
    {children}
  </Stack>
);

const OrdersHeader = ({
  genericTradeParams,
  activeOrderCount,
}: {
  genericTradeParams: GenericTradeParams['genericTradeParams'];
  activeOrderCount: number;
}) => (
  <HStack
    px="4"
    py="3"
    justifyContent="space-between"
    borderBottom="1px solid"
    borderBottomColor="v2.border.default"
  >
    <HStack gap="2" minW={0}>
      <Box
        w="3px"
        h="24px"
        borderRadius="2px"
        bg={genericTradeParams.isLong ? 'v2.long.default' : 'v2.short.default'}
      />
      <Stack gap="0" minW={0}>
        <HStack gap="2">
          <Text textStyle="headings.default">
            {genericTradeParams.isLong ? 'Long' : 'Short'}{' '}
            {genericTradeParams.pair.baseName} TP/SL
          </Text>
          <TradeLeverageBadge genericTradeParams={genericTradeParams} />
        </HStack>
        <Text textStyle="v2.meta" color="v2.text.muted">
          {activeOrderCount} active decrease orders
        </Text>
      </Stack>
    </HStack>
    <Dialog.CloseTrigger boxShadow="none" bg="none">
      <Icon w="20px" h="20px" color="v2.text.muted">
        <X />
      </Icon>
    </Dialog.CloseTrigger>
  </HStack>
);

const AddDecreaseOrderView = ({
  genericTradeParams,
  onBack,
  children,
}: {
  genericTradeParams: GenericTradeParams['genericTradeParams'];
  onBack: () => void;
  children: React.ReactNode;
}) => (
  <>
    <HStack
      px="4"
      py="3"
      justifyContent="space-between"
      borderBottom="1px solid"
      borderBottomColor="v2.border.default"
    >
      <HStack gap="2" minW={0}>
        <Button
          variant="ghost"
          size="small"
          px="1"
          minW="28px"
          onClick={onBack}
        >
          <Icon boxSize="16px" color="v2.text.muted">
            <ArrowLeft />
          </Icon>
        </Button>
        <Stack gap="0" minW={0}>
          <HStack gap="2">
            <Text textStyle="headings.default">Add TP/SL</Text>
            <TradeLeverageBadge genericTradeParams={genericTradeParams} />
          </HStack>
          <Text textStyle="v2.meta" color="v2.text.muted">
            {genericTradeParams.isLong ? 'Long' : 'Short'}{' '}
            {genericTradeParams.pair.baseName}
          </Text>
        </Stack>
      </HStack>
      <Dialog.CloseTrigger boxShadow="none" bg="none">
        <Icon w="20px" h="20px" color="v2.text.muted">
          <X />
        </Icon>
      </Dialog.CloseTrigger>
    </HStack>
    <Stack maxH="520px" overflowY="auto" p="3">
      {children}
    </Stack>
  </>
);

const EditDecreaseOrderView = ({
  order,
  genericTradeParams,
  onBack,
  children,
}: {
  order: DecreaseOrderRow;
  genericTradeParams: GenericTradeParams['genericTradeParams'];
  onBack: () => void;
  children: React.ReactNode;
}) => (
  <>
    <HStack
      px="4"
      py="3"
      justifyContent="space-between"
      borderBottom="1px solid"
      borderBottomColor="v2.border.default"
    >
      <HStack gap="2" minW={0}>
        <Button
          variant="ghost"
          size="small"
          px="1"
          minW="28px"
          onClick={onBack}
        >
          <Icon boxSize="16px" color="v2.text.muted">
            <ArrowLeft />
          </Icon>
        </Button>
        <Stack gap="0" minW={0}>
          <HStack gap="2">
            <Text textStyle="headings.default">Edit {order.kind}</Text>
            <TradeLeverageBadge genericTradeParams={genericTradeParams} />
          </HStack>
          <Text textStyle="v2.meta" color="v2.text.muted">
            {genericTradeParams.isLong ? 'Long' : 'Short'}{' '}
            {genericTradeParams.pair.baseName}
          </Text>
        </Stack>
      </HStack>
      <Dialog.CloseTrigger boxShadow="none" bg="none">
        <Icon w="20px" h="20px" color="v2.text.muted">
          <X />
        </Icon>
      </Dialog.CloseTrigger>
    </HStack>
    <Stack maxH="520px" overflowY="auto" p="3">
      {children}
    </Stack>
  </>
);

const metricValueStyle = {
  ...valueStyle,
  textAlign: 'start',
};

const ZERO_ORDER_HASH =
  '0x0000000000000000000000000000000000000000000000000000000000000000';

const LiqPriceValue = ({ genericTradeParams }: GenericTradeParams) => {
  const positionLiqPrice = useLiqPrice({ genericTradeParams });
  const priceDisplayDecimals = useTokenDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
    genericTradeParams.pair.priceDisplayDecimals,
  );

  return (
    <AmountUsd
      css={metricValueStyle}
      value={positionLiqPrice}
      opts={{
        displayDecimals: priceDisplayDecimals,
      }}
      data-cy="MergedTpSlOrdersLiqPrice"
      data-cy-value={positionLiqPrice}
    />
  );
};

const DecreaseOrderItem = ({
  genericTradeParams,
  order,
  onEdit,
}: {
  genericTradeParams: GenericTradeParams['genericTradeParams'];
  order: DecreaseOrderRow;
  onEdit: () => void;
}) => {
  const triggerPrice = toReadableValue(order.triggerPrice, USD_DECIMALS);
  const closeQty = toReadableValue(order.closeQty, PAIR_AMOUNT_DECIMALS);
  const closePct = genericTradeParams.size.isZero()
    ? undefined
    : closeQty.div(genericTradeParams.size).times(100);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
    genericTradeParams.pair.priceDisplayDecimals,
  );
  const sizeDisplayDecimals = useTokenSizeDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
  );
  const livePairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);
  const closeQtyUsd = closeQty.multipliedBy(
    livePairMarketPrice ?? genericTradeParams.entryPrice,
  );
  const { cancel, isPending } = useCancelDecreaseOrder();
  const canCancel = order.orderHash !== ZERO_ORDER_HASH;

  return (
    <Grid
      gridTemplateColumns="104px minmax(0, 1fr) minmax(0, 1.25fr) 72px"
      alignItems="center"
      gap="2"
      p="3"
      border="1px solid"
      borderColor="v2.border.default"
      borderRadius="4px"
      bg="v2.bg.surface1"
    >
      <OrderKindPill kind={order.kind} />
      <Stack gap="1" minW={0}>
        <Text textStyle="v2.meta" color="v2.text.muted">
          Trigger price
        </Text>
        <AmountUsd
          value={triggerPrice}
          css={orderRowValueStyle}
          opts={{ displayDecimals: priceDisplayDecimals }}
        />
      </Stack>
      <Stack gap="1" minW={0}>
        <Text textStyle="v2.meta" color="v2.text.muted">
          Close qty
        </Text>
        <HStack gap="1">
          <Amount
            value={closeQty}
            css={orderRowValueStyle}
            opts={{
              displayDecimals: sizeDisplayDecimals,
              suffix: ` ${genericTradeParams.pair.baseSymbol}`,
            }}
          />
          <Text textStyle="numbers.xsmall" color="v2.text.subtle">
            {closePct ? `(${closePct.toFixed(2)}%)` : ''}
          </Text>
        </HStack>
        <AmountUsd
          value={closeQtyUsd}
          textStyle="numbers.xsmall"
          color="v2.text.subtle"
          opts={{ prefix: '$' }}
        />
      </Stack>
      <HStack gap="1" justifyContent="flex-end">
        <Button
          variant="ghost"
          size="small"
          p="0"
          minW="28px"
          h="28px"
          onClick={onEdit}
          aria-label="Update order"
        >
          <Icon boxSize="14px" color="v2.text.secondary">
            <Edit />
          </Icon>
        </Button>
        <Button
          variant="ghost"
          size="small"
          p="0"
          minW="28px"
          h="28px"
          loading={isPending}
          disabled={!canCancel}
          onClick={() => canCancel && cancel(order.orderHash)}
          aria-label="Cancel order"
        >
          <Icon boxSize="14px" color="v2.text.secondary">
            <XCircle />
          </Icon>
        </Button>
      </HStack>
    </Grid>
  );
};

const OrderKindPill = ({ kind }: { kind: DecreaseOrderRow['kind'] }) => (
  <Box
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    w="88px"
    h="22px"
    px="2"
    borderRadius="2px"
    border="1px solid"
    borderColor={kind === 'TP' ? 'v2.long.border' : 'v2.short.border'}
    bg={kind === 'TP' ? 'v2.long.dim' : 'v2.short.dim'}
    color={kind === 'TP' ? 'v2.long.default' : 'v2.short.default'}
    textStyle="v2.meta"
    fontWeight="600"
  >
    {kind}
  </Box>
);

const orderRowValueStyle = {
  ...valueStyle,
  textAlign: 'start',
};

const DecreaseOrderForm = ({
  genericTradeParams,
  collateralMarketPrice,
  pairMarketPrice,
  onDone,
}: {
  genericTradeParams: GenericTradeParams['genericTradeParams'];
  collateralMarketPrice: BigNumber;
  pairMarketPrice: BigNumber;
  onDone: () => void;
}) => {
  const [tp, setTp] = useState<TpSlValueState>(() => ({
    priceValue: '',
    pnlPercentage: undefined,
    deltaPnl: undefined,
    isLong: false,
    isPnlPercentage: false,
  }));
  const [sl, setSl] = useState<TpSlValueState>(() => ({
    priceValue: '',
    pnlPercentage: undefined,
    deltaPnl: undefined,
    isLong: false,
    isPnlPercentage: false,
  }));
  const { batchCreate, isPending } = useBatchCreateDecreaseOrders({
    onSuccess: onDone,
  });
  const [closeQtyPercent, setCloseQtyPercent] = useState(100);
  const tradePair = useTradePairInfosByPairBase(
    genericTradeParams.pair.address,
  );
  const positionSizeUsd = genericTradeParams.getSizeUsd(pairMarketPrice);
  const spreadPercentageInfo = useSpreadPercentage({
    tradePair,
    positionSize: positionSizeUsd,
    pairPrice: pairMarketPrice,
  });
  const spreadPercentage = calculateSpreadPercentage({
    isLong: genericTradeParams.isLong,
    isClosePrice: true,
    spreadPercentageInfo,
  });
  const leverageValue = genericTradeParams
    .getLeverage({
      collateralTokenMarketPrice: collateralMarketPrice,
    })
    .toNumber();
  const maxTpRatios = useGetTargetMaxTpRatios(
    genericTradeParams.pair.address,
    leverageValue,
  );
  const collateralUsdBeforeOpenPositionFees =
    genericTradeParams.getCollateralUsdBeforeOpenPositionFees(
      collateralMarketPrice,
    );
  const tpValidation = useEditTpValidation({
    genericTradeParams,
    tp,
    maxTpRatios,
    spreadPercentage,
    leverage: leverageValue,
  });
  const slValidation = useEditSlValidation({
    genericTradeParams,
    sl,
  });
  const hasTp = tp.priceValue.trim() !== '';
  const hasSl = sl.priceValue.trim() !== '';
  const tpPrice = new BigNumber(tp.priceValue);
  const slPrice = new BigNumber(sl.priceValue);
  const isDisabled =
    (!hasTp && !hasSl) ||
    (hasTp && (!tpPrice.isFinite() || tpPrice.lte(0))) ||
    (hasSl && (!slPrice.isFinite() || slPrice.lte(0))) ||
    (hasTp && tpValidation?.valid === false) ||
    (hasSl && slValidation?.valid === false);
  const selectedCloseQty = useMemo(() => {
    return genericTradeParams.size.multipliedBy(closeQtyPercent).div(100);
  }, [closeQtyPercent, genericTradeParams.size]);
  const livePairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);
  const selectedCloseQtyUsd = selectedCloseQty.multipliedBy(
    livePairMarketPrice ?? pairMarketPrice,
  );
  const sizeDisplayDecimals = useTokenSizeDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
  );
  const closeQty = toBigValue(selectedCloseQty, PAIR_AMOUNT_DECIMALS);

  return (
    <Stack gap="2">
      <Box py="0">
        <EditTpSl
          tp={tp}
          setTp={setTp}
          sl={sl}
          setSl={setSl}
          genericTradeParams={genericTradeParams}
          isPositionType
          maxTpRatios={maxTpRatios}
          spreadPercentage={spreadPercentage}
          leverage={leverageValue}
          collateralUsd={collateralUsdBeforeOpenPositionFees}
          tpValidation={tpValidation}
          slValidation={slValidation}
        />
      </Box>
      <CloseQtyPercentageSelector
        value={closeQtyPercent}
        onChange={setCloseQtyPercent}
        mt="0"
        flush
      />
      <Grid
        gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr) 136px"
        alignItems="center"
        gap="2"
        pt="3"
        borderTop="1px solid"
        borderTopColor="v2.border.hairline"
      >
        <SummaryStat label="Close qty">
          <HStack gap="1" minW={0}>
            <Amount
              value={selectedCloseQty}
              css={valueStyle}
              textAlign="start"
              opts={{
                displayDecimals: sizeDisplayDecimals,
                suffix: ` ${genericTradeParams.pair.baseSymbol}`,
              }}
            />
            <AmountUsd
              value={selectedCloseQtyUsd}
              textStyle="numbers.xsmall"
              color="v2.text.subtle"
              opts={{
                prefix: '($',
                suffix: ')',
              }}
            />
          </HStack>
        </SummaryStat>
        <SummaryStat label="Leverage" align="end">
          {`${formatLeverage(leverageValue)}x`}
        </SummaryStat>
        <RequireConnectionClickable>
          <Button
            variant="primary"
            w="full"
            h="40px"
            loading={isPending}
            disabled={isDisabled}
            onClick={() => {
              const orders = [
                ...(hasTp
                  ? [
                      {
                        kind: DecreaseOrderKind.TP,
                        triggerPrice: toBigValue(tp.priceValue, USD_DECIMALS),
                        closeQty,
                        broker: 0,
                      },
                    ]
                  : []),
                ...(hasSl
                  ? [
                      {
                        kind: DecreaseOrderKind.SL,
                        triggerPrice: toBigValue(sl.priceValue, USD_DECIMALS),
                        closeQty,
                        broker: 0,
                      },
                    ]
                  : []),
              ];

              batchCreate({
                positionHash: genericTradeParams.id,
                orders,
              });
            }}
          >
            Place Order
          </Button>
        </RequireConnectionClickable>
      </Grid>
      {/* helper note reads as fine print on its own bottom line */}
      <Text textStyle="v2.meta" color="v2.text.subtle" pt="1">
        Create one or two decrease orders with the same close qty.
      </Text>
    </Stack>
  );
};

type BatchDecreaseOrderInput = {
  kind: DecreaseOrderKind;
  triggerPrice: bigint;
  closeQty: bigint;
  broker: number;
};

const useBatchCreateDecreaseOrders = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  const chainId = useDataChainId();
  const invalidatePositionQuery = useInvalidatePositionQuery();

  // OneClick V2 integration
  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingBatchCreateDecreaseOrders: isSubmittingV2,
    sendBatchCreateDecreaseOrders: sendBatchCreateDecreaseOrdersV2,
  } = useOneClickTradingV2();
  const mutationToastInfo = useMutationToast();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  // Legacy on-chain path
  const { writeContract, isPending: isPendingOnchain } = useTxnWriteContract({
    onSuccess: () => {
      invalidatePositionQuery();
      onSuccess?.();
    },
  });

  const handleOneClickBatchCreate = useCallback(
    async ({
      positionHash,
      orders,
    }: {
      positionHash: `0x${string}`;
      orders: BatchDecreaseOrderInput[];
    }) => {
      if (!canUseOneClickTradingV2) return false;
      if (orders.length === 0) return false;

      setIsAwaitingOneClickResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 TP/SL order creation in progress',
      });

      try {
        // Convert orders to DecreaseOrderInput format for OneClick V2
        const v2Orders = orders.map((order) => [
          order.kind,
          order.triggerPrice,
          order.closeQty,
          order.broker,
        ] as const);

        const intentHash = await sendBatchCreateDecreaseOrdersV2(
          positionHash,
          v2Orders,
        );

        const status = await pollV2IntentStatus(intentHash);
        if (!status.success) {
          throw new Error(
            status.reason || 'One-click V2 batch create decrease orders failed',
          );
        }

        invalidatePositionQuery();

        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'TP/SL orders created successfully via one-click V2',
          txHash: status.txnHash as `0x${string}`,
        });

        onSuccess?.();
        return true; // OneClick V2 succeeded
      } catch (err) {
        const message =
          err instanceof Error
            ? extractErrorMessage(err.message) ?? err.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
          onErrorToastOpts: {
            description: 'One-click V2 TP/SL order creation failed, falling back to on-chain',
          },
        });

        console.warn('[OneClickV2] Batch create decrease orders failed:', err);
        return false; // Fall back to on-chain
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickResult(false);
      }
    },
    [
      canUseOneClickTradingV2,
      sendBatchCreateDecreaseOrdersV2,
      invalidatePositionQuery,
      onSuccess,
    ],
  );

  return {
    isPending: isSubmittingV2 || isAwaitingOneClickResult || isPendingOnchain,
    batchCreate: async ({
      positionHash,
      orders,
    }: {
      positionHash: `0x${string}`;
      orders: BatchDecreaseOrderInput[];
    }) => {
      if (orders.length === 0) return;

      // Try OneClick V2 first
      const oneClickSuccess = await handleOneClickBatchCreate({
        positionHash,
        orders,
      });

      if (oneClickSuccess) return;

      // Fallback to on-chain
      const address = getContractAddress('LeverUp', chainId);
      if (!address) throw new Error('LeverUp contract address missing');

      return writeContract({
        abi: TradingPortalFacet,
        address,
        functionName: 'batchCreateDecreaseOrders',
        args: [positionHash, orders],
      });
    },
  };
};

const useCancelDecreaseOrder = () => {
  const chainId = useDataChainId();
  const invalidatePositionQuery = useInvalidatePositionQuery();

  // OneClick V2 integration
  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingCancelDecreaseOrder: isSubmittingV2,
    sendCancelDecreaseOrder: sendCancelDecreaseOrderV2,
  } = useOneClickTradingV2();
  const mutationToastInfo = useMutationToast();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  const { writeContract, isPending } = useTxnWriteContract({
    onSuccess: invalidatePositionQuery,
  });

  const handleOneClickCancel = useCallback(
    async (orderHash: `0x${string}`) => {
      if (!canUseOneClickTradingV2) return false;

      setIsAwaitingOneClickResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 cancel decrease order in progress',
      });

      try {
        const intentHash = await sendCancelDecreaseOrderV2(orderHash);

        const status = await pollV2IntentStatus(intentHash);
        if (!status.success) {
          throw new Error(
            status.reason || 'One-click V2 cancel decrease order failed',
          );
        }

        invalidatePositionQuery();

        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'Decrease order cancelled successfully via one-click V2',
          txHash: status.txnHash as `0x${string}`,
        });

        return true;
      } catch (err) {
        const message =
          err instanceof Error
            ? extractErrorMessage(err.message) ?? err.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
          onErrorToastOpts: {
            description:
              'One-click V2 cancel failed, falling back to on-chain',
          },
        });

        console.warn('[OneClickV2] Cancel decrease order failed:', err);
        return false;
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickResult(false);
      }
    },
    [
      canUseOneClickTradingV2,
      sendCancelDecreaseOrderV2,
      invalidatePositionQuery,
    ],
  );

  return {
    isPending: isSubmittingV2 || isAwaitingOneClickResult || isPending,
    cancel: async (orderHash: `0x${string}`) => {
      // Try OneClick V2 first
      const oneClickSuccess = await handleOneClickCancel(orderHash);
      if (oneClickSuccess) return;

      // Fallback to on-chain
      const address = getContractAddress('LeverUp', chainId);
      if (!address) throw new Error('LeverUp contract address missing');

      return writeContract({
        abi: TradingPortalFacet,
        address,
        functionName: 'cancelDecreaseOrder',
        args: [orderHash],
      });
    },
  };
};
